import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormGroup,
  Tooltip,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { Refresh as RefreshIcon, Launch as LaunchIcon, DateRange as DateRangeIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import apiService from '../../services/apiService';
import { useUser } from '../../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import PageContainer from '../../components/PageContainer';
import { LoadingState } from '../../components/LoadingState';

// Date range types (matching analytics)
type DateRangeType = 'last_1_day' | 'last_7_days' | 'last_30_days' | 'last_90_days' | 'last_12_months' | 'custom';

interface DateRange {
  date_range: DateRangeType;
  startDate?: Date;
  endDate?: Date;
}

interface MergeJob {
  id: number;
  created: string;
  source_email_id: number;
  source_connect_url: string;
  target_connect_url: string;
  client_id: number;
  result: string | null;
  target_email_id: number | null;
  completed: string | null;
}

interface Client {
  id: number;
  url: string | null;
  connect_name: string | null;
  agency_name: string | null;
  active: boolean;
}

interface Group {
  id: number;
  name: string;
  description: string | null;
  member_count: number;
  client_count: number;
}

const MailMerge: React.FC = () => {
  const [sourceUrl, setSourceUrl] = useState('map100.travelminds.com');
  const [emailId, setEmailId] = useState('');
  const [targetConnects, setTargetConnects] = useState<{ [key: number]: boolean }>({});
  const [mergeJobs, setMergeJobs] = useState<MergeJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState<number[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<number | string | null>(null);
  const [groupClients, setGroupClients] = useState<Client[]>([]);
  const [loadingGroupClients, setLoadingGroupClients] = useState(false);
  const [connectInstances, setConnectInstances] = useState<Client[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 1);  // last 1 day
    return {
      date_range: 'last_1_day',
      startDate: start,
      endDate: end
    };
  });
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState('6');
  const timerRef = useRef<number | null>(null);
  const auth = useUser();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [runningJobs, setRunningJobs] = useState<Set<number>>(new Set());

  // Add custom styles for number input spinner
  useEffect(() => {
    // Add custom styles for large spinner
    const style = document.createElement('style');
    style.textContent = `
      .large-spinner::-webkit-inner-spin-button {
        height: 100%;
        opacity: 1;
        margin-left: 5px;
        transform: scale(1.5);
      }
      
      /* Ensure spinner is visible across browsers */
      input[type=number].large-spinner {
        -moz-appearance: textfield;
        appearance: textfield;
      }
      
      input[type=number].large-spinner::-webkit-outer-spin-button,
      input[type=number].large-spinner::-webkit-inner-spin-button {
        -webkit-appearance: inner-spin-button !important;
        opacity: 1;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Fetch data function
  const fetchData = async (includeClients = false) => {
    try {
      setLoading(true);
      
      if (!includeClients) {
        // Only refresh jobs data
        setLoadingJobs(true);
        const response = await apiService.get<MergeJob[]>('/api/merge-jobs');
        // Only update state if data has changed
        const currentJobsStr = JSON.stringify(mergeJobs);
        const newJobsStr = JSON.stringify(response.data);
        if (currentJobsStr !== newJobsStr) {
          setMergeJobs(response.data);
        }
        setLoadingJobs(false);
      } else {
        // Fetch both jobs and clients
        setLoadingJobs(true);
        const [jobsResponse, clientsResponse] = await Promise.all([
          apiService.get<MergeJob[]>('/api/merge-jobs'),
          apiService.get<Client[]>('/api/admin/clients')
        ]);
        
        console.log('Raw clients response:', clientsResponse.data);
        
        // Filter clients that are active and have connect_name
        const filteredClients = clientsResponse.data.filter(client => {
          
          // Client must be active
          if (client.active === false) {
            console.log(`Client ${client.id} filtered out: not active`);
            return false;
          }
          
          // Client must have either connect_name or url
          if (!client.connect_name ) {
            console.log(`Client ${client.id} filtered out: no connect_name`);
            return false;
          }

          if (client.connect_name === 'map100.travelminds.com') {
            console.log(`Client ${client.id} filtered out: map100.travelminds.com`);
            return false;
          }
          
          return true;
        });
        
        console.log('Filtered clients:', filteredClients);
        
        setMergeJobs(jobsResponse.data);
        setClients(filteredClients);
        console.log('Initial data loaded:', {
          clientsCount: filteredClients.length,
          selectedGroup,
          jobsCount: jobsResponse.data.length
        });
        // Connect instances will be updated by useEffect when clients state changes
      }
    } catch (error: any) {
      console.error('Error fetching data:', error);
      if (error.response) {
        console.error('API Error Response:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        });
      }
      // Only show error on manual refresh or initial load
      if (includeClients) {
        setError('Failed to load data');
      }
    } finally {
      setLoading(false);
      setLoadingJobs(false);
    }
  };

  // Group-related functions
  const fetchGroups = async () => {
    try {
      const response = await apiService.get('/api/groups/admin/all');
      setGroups(response.data);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const fetchGroupClients = async (groupId: number) => {
    try {
      console.log('fetchGroupClients called for groupId:', groupId);
      setLoadingGroupClients(true);
      const response = await apiService.get(`/api/groups/${groupId}/clients`);
      // Extract client data from GroupClientResponse objects
      const clients = response.data.map((groupClient: any) => groupClient.client).filter(Boolean);
      console.log('Fetched clients for group', groupId, ':', clients.length, 'clients');
      setGroupClients(clients);
    } catch (error) {
      console.error('Error fetching group clients:', error);
      setGroupClients([]);
    } finally {
      setLoadingGroupClients(false);
    }
  };

  const fetchUngroupedClients = async () => {
    try {
      console.log('fetchUngroupedClients called');
      setLoadingGroupClients(true);
      
      // Get all clients that are not in any group
      // We'll need to fetch all clients and filter out those that are in groups
      const allClientsResponse = await apiService.get('/api/clients');
      const allClients = allClientsResponse.data;
      
      // Get all group client relationships
      const groupClientsResponses = await Promise.all(
        groups.map(group => apiService.get(`/api/groups/${group.id}/clients`))
      );
      
      // Extract all client IDs that are in groups
      const groupedClientIds = new Set();
      groupClientsResponses.forEach(response => {
        response.data.forEach((groupClient: any) => {
          if (groupClient.client && groupClient.client.id) {
            groupedClientIds.add(groupClient.client.id);
          }
        });
      });
      
      // Filter out clients that are in groups
      const ungroupedClients = allClients.filter((client: any) => !groupedClientIds.has(client.id));
      
      console.log('Fetched ungrouped clients:', ungroupedClients.length, 'clients');
      setGroupClients(ungroupedClients);
    } catch (error) {
      console.error('Error fetching ungrouped clients:', error);
      setGroupClients([]);
    } finally {
      setLoadingGroupClients(false);
    }
  };

  const handleGroupChange = async (groupId: number | string | null) => {
    setSelectedGroup(groupId);
    if (groupId === 'ungrouped') {
      // Fetch clients that are not in any group
      await fetchUngroupedClients();
    } else if (typeof groupId === 'number') {
      await fetchGroupClients(groupId);
    } else {
      setGroupClients([]);
    }
  };

  // Simple function to get connect instances (trust the list we have)
  const getConnectInstances = () => {
    return connectInstances;
  };

  // Update connect instances when group changes or on page load
  const updateConnectInstances = () => {
    console.log('updateConnectInstances called:', {
      selectedGroup,
      clientsCount: clients.length,
      groupClientsCount: groupClients.length
    });
    
    if (selectedGroup === null) {
      // Use all clients (filtered)
      const filteredClients = clients.filter(client => {
        const name = (client?.url || client?.agency_name || '').toLowerCase();
        return !name.includes('source');
      }).sort((a, b) => {
        const nameA = (a?.url || a?.agency_name || '').toLowerCase();
        const nameB = (b?.url || b?.agency_name || '').toLowerCase();
        
        const isATravel = nameA.includes('travelminds') || nameA.includes('travel minds');
        const isBTravel = nameB.includes('travelminds') || nameB.includes('travel minds');
        
        if (isATravel && !isBTravel) return -1;
        if (!isATravel && isBTravel) return 1;
        
        return nameA.localeCompare(nameB);
      });
      console.log('Setting connect instances from all clients:', filteredClients.length);
      setConnectInstances(filteredClients);
    } else if (selectedGroup === 'ungrouped') {
      // Use ungrouped clients (filtered)
      console.log('Using ungrouped clients for selectedGroup:', selectedGroup, 'groupClients:', groupClients.length);
      const filteredClients = groupClients.filter(client => {
        const name = (client?.url || client?.agency_name || '').toLowerCase();
        return !name.includes('source');
      }).sort((a, b) => {
        const nameA = (a?.url || a?.agency_name || '').toLowerCase();
        const nameB = (b?.url || b?.agency_name || '').toLowerCase();
        
        const isATravel = nameA.includes('travelminds') || nameA.includes('travel minds');
        const isBTravel = nameB.includes('travelminds') || nameB.includes('travel minds');
        
        if (isATravel && !isBTravel) return -1;
        if (!isATravel && isBTravel) return 1;
        
        return nameA.localeCompare(nameB);
      });
      console.log('Setting connect instances from ungrouped clients:', filteredClients.length, 'for group:', selectedGroup);
      console.log('Ungrouped client details:', filteredClients.map(c => ({ id: c.id, url: c.url, agency_name: c.agency_name })));
      setConnectInstances(filteredClients);
    } else {
      // Use group clients (filtered)
      console.log('Using group clients for selectedGroup:', selectedGroup, 'groupClients:', groupClients.length);
      const filteredClients = groupClients.filter(client => {
        const name = (client?.url || client?.agency_name || '').toLowerCase();
        return !name.includes('source');
      }).sort((a, b) => {
        const nameA = (a?.url || a?.agency_name || '').toLowerCase();
        const nameB = (b?.url || b?.agency_name || '').toLowerCase();
        
        const isATravel = nameA.includes('travelminds') || nameA.includes('travel minds');
        const isBTravel = nameB.includes('travelminds') || nameB.includes('travel minds');
        
        if (isATravel && !isBTravel) return -1;
        if (!isATravel && isBTravel) return 1;
        
        return nameA.localeCompare(nameB);
      });
      console.log('Setting connect instances from group clients:', filteredClients.length, 'for group:', selectedGroup);
      console.log('Group client details:', filteredClients.map(c => ({ id: c.id, url: c.url, agency_name: c.agency_name })));
      setConnectInstances(filteredClients);
    }
  };

  // Get jobs to display based on group selection and date range (memoized)
  const displayJobs = useMemo(() => {
    console.log('getDisplayJobs called:', {
      selectedGroup,
      groupClientsLength: groupClients.length,
      mergeJobsLength: mergeJobs.length,
      dateRange
    });
    
    let filteredJobs = mergeJobs;
    
    // Filter by group if selected
    if (selectedGroup !== null) {
      const groupClientIds = new Set(
        groupClients
          .map(client => client.id)
          .filter(Boolean) // Remove any null/undefined IDs
      );
      
      console.log('Group filtering debug:', {
        selectedGroup,
        groupClients: groupClients.length,
        groupClientIds: Array.from(groupClientIds),
        totalJobs: mergeJobs.length
      });
      
      filteredJobs = filteredJobs.filter(job => groupClientIds.has(job.client_id));
      
      console.log('Jobs matching group:', filteredJobs.length, 'out of', mergeJobs.length);
    }
    
    // Filter by date range if specified
    if (dateRange.startDate || dateRange.endDate) {
      const beforeDateFilter = filteredJobs.length;
      filteredJobs = filteredJobs.filter(job => {
        const jobDate = new Date(job.created);
        const startMatch = !dateRange.startDate || jobDate >= dateRange.startDate;
        const endMatch = !dateRange.endDate || jobDate <= dateRange.endDate;
        return startMatch && endMatch;
      });
      console.log('Date filtering:', {
        beforeDateFilter,
        afterDateFilter: filteredJobs.length,
        dateRange: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate
        }
      });
    }
    
    console.log('Final filtered jobs count:', filteredJobs.length);
    return filteredJobs;
  }, [selectedGroup, groupClients, mergeJobs, dateRange]);

  // Initial data load
  useEffect(() => {
    console.log('Initial data load starting...');
    fetchData(true);
    fetchGroups();
  }, []);

  // Update connect instances when clients are loaded (for initial load)
  useEffect(() => {
    console.log('useEffect triggered for clients change (initial load):', {
      selectedGroup,
      clientsLength: clients.length,
      connectInstancesLength: connectInstances.length
    });
    
    // Only update if we have clients, no group is selected, and no connect instances yet
    if (clients.length > 0 && selectedGroup === null && connectInstances.length === 0) {
      console.log('Initial load: updating connect instances from clients');
      updateConnectInstances();
    }
  }, [clients]);

  // Update connect instances when group selection changes
  useEffect(() => {
    console.log('useEffect triggered for selectedGroup/groupClients change:', {
      selectedGroup,
      groupClientsLength: groupClients.length
    });
    updateConnectInstances();
    // Debug: Log when group clients change
    if (selectedGroup !== null) {
      console.log('Group clients updated:', {
        selectedGroup,
        groupClientsCount: groupClients.length,
        groupClients: groupClients.map(c => ({ id: c.id, connect_name: c.connect_name, url: c.url }))
      });
    }
  }, [selectedGroup, groupClients]);

  // Auto refresh effect
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (autoRefresh && refreshInterval) {
      const interval = parseInt(refreshInterval) * 1000;
      intervalId = setInterval(async () => {
        // Check if any jobs are incomplete before refreshing
        const hasIncompleteJobs = mergeJobs.some(job => !job.completed);
        if (hasIncompleteJobs) {
          await fetchData(false); // Only refresh jobs during auto-refresh
        } else {
          setAutoRefresh(false);
        }
      }, interval);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [autoRefresh, refreshInterval, mergeJobs]);

  // Handle auto-refresh toggle
  const handleAutoRefreshChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAutoRefresh(event.target.checked);
  };

  // Handle refresh interval change
  const handleRefreshIntervalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      setRefreshInterval(value);
    }
  };

  // Calculate if all jobs are completed
  const areAllJobsCompleted = mergeJobs.length > 0 && 
    mergeJobs.every(job => job.completed !== null);
    
  // Turn off auto-refresh when all jobs are completed
  useEffect(() => {
    if (areAllJobsCompleted && autoRefresh) {
      setAutoRefresh(false);
    }
  }, [areAllJobsCompleted, autoRefresh]);

  // Clear running jobs when they complete
  useEffect(() => {
    setRunningJobs(prev => {
      const newSet = new Set(prev);
      let hasChanges = false;
      
      // Check each running job
      prev.forEach(jobId => {
        const job = mergeJobs.find(j => j.id === jobId);
        if (job && job.completed) {
          newSet.delete(jobId);
          hasChanges = true;
        }
      });
      
      return hasChanges ? newSet : prev;
    });
  }, [mergeJobs]);

  // Handle target connect checkbox changes
  const handleTargetConnectChange = (clientId: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    console.log('Target connect change:', { 
      clientId, 
      checked,
      client: clients.find(c => c.id === clientId)
    });
    setTargetConnects(prev => {
      const newState = { ...prev, [clientId]: checked };
      console.log('New targetConnects state:', newState);
      return newState;
    });
  };

  // Handle select all target connects - simple and fast
  const handleSelectAllTargets = (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('Select all targets:', event.target.checked);
    console.log('Connect instances count:', connectInstances.length);
    
    setTargetConnects(prev => {
      const newState = { ...prev };
      // Simply toggle each connect instance in our trusted list
      connectInstances.forEach(client => {
        newState[client.id] = event.target.checked;
      });
      return newState;
    });
  };

  // Handle job selection
  const handleJobSelection = (jobId: number) => {
    console.log('handleJobSelection called with jobId:', jobId);
    console.log('Current selectedJobs:', selectedJobs);
    
    setSelectedJobs(prev => {
      const newSelection = prev.includes(jobId) 
        ? prev.filter(id => id !== jobId)
        : [...prev, jobId];
      
      console.log('New selectedJobs:', newSelection);
      return newSelection;
    });
  };

  // Handle select all jobs
  const handleSelectAll = () => {
    const displayJobIds = displayJobs.map(job => job.id);
    const allDisplayJobsSelected = displayJobIds.every(id => selectedJobs.includes(id));
    
    if (allDisplayJobsSelected) {
      // Deselect all display jobs
      setSelectedJobs(prev => prev.filter(id => !displayJobIds.includes(id)));
    } else {
      // Select all display jobs (keep existing selections from other pages)
      setSelectedJobs(prev => {
        const newSelections = [...prev];
        displayJobIds.forEach(id => {
          if (!newSelections.includes(id)) {
            newSelections.push(id);
          }
        });
        return newSelections;
      });
    }
  };

  const showMessage = (message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
    // Auto hide after 5 seconds
    setTimeout(() => {
      setSnackbar(prev => ({ ...prev, open: false }));
    }, 5000);
  };

  // Handle delete selected jobs
  const handleDeleteSelected = async () => {
    if (!selectedJobs.length) return;

    try {
      setLoading(true);
      await apiService.delete('/api/merge-jobs', {
        data: { ids: selectedJobs }
      });

      // Refresh jobs list
      await fetchData(false);

      setSelectedJobs([]);
      showMessage('Selected jobs deleted successfully');
    } catch (error) {
      console.error('Error deleting jobs:', error);
      showMessage('Failed to delete selected jobs', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle run mail merge for selected jobs (or all pending if none selected)
  const handleRunMailMerge = async () => {
    try {
      setLoading(true);
      
      // Determine which jobs to trigger
      let jobsToTrigger: number[];
      
      if (selectedJobs.length > 0) {
        // Use selected jobs
        jobsToTrigger = selectedJobs;
      } else {
        // Use all pending (uncompleted) jobs
        jobsToTrigger = mergeJobs
          .filter(job => !job.completed)
          .map(job => job.id);
      }
      
      if (jobsToTrigger.length === 0) {
        showMessage('No pending jobs to trigger', 'error');
        return;
      }
      
      // Filter out jobs that are already running
      const notRunningJobs = jobsToTrigger.filter(jobId => !runningJobs.has(jobId));
      const alreadyRunningLocal = jobsToTrigger.filter(jobId => runningJobs.has(jobId));
      
      if (notRunningJobs.length === 0) {
        if (alreadyRunningLocal.length > 0) {
          showMessage(`${alreadyRunningLocal.length} job${alreadyRunningLocal.length > 1 ? 's are' : ' is'} already running`, 'error');
        } else {
          showMessage('No pending jobs to trigger', 'error');
        }
        return;
      }
      
      // Add jobs to running set
      setRunningJobs(prev => {
        const newSet = new Set(prev);
        notRunningJobs.forEach(jobId => newSet.add(jobId));
        return newSet;
      });
      
      // Show message if some jobs were skipped
      if (alreadyRunningLocal.length > 0) {
        showMessage(`Skipped ${alreadyRunningLocal.length} job${alreadyRunningLocal.length > 1 ? 's' : ''} already running, triggering ${notRunningJobs.length}...`, 'success');
      }
      
      // Trigger Prefect for each job
      const promises = notRunningJobs.map(jobId => 
        apiService.post(`/api/merge-jobs/trigger?job_id=${jobId}`)
      );
      
      const results = await Promise.allSettled(promises);
      
      const successful: number[] = [];
      const alreadyRunning: number[] = [];
      const failed: any[] = [];
      
      results.forEach((result, index) => {
        const jobId = notRunningJobs[index];
        
        if (result.status === 'fulfilled') {
          successful.push(jobId);
        } else if (result.reason?.response?.status === 409) {
          // Job is already running in Prefect
          alreadyRunning.push(jobId);
          // Add to running set to prevent retry
          setRunningJobs(prev => new Set(prev).add(jobId));
        } else {
          failed.push({ jobId, error: result.reason });
        }
      });
      
      if (successful.length > 0 || alreadyRunning.length > 0) {
        let message = '';
        if (successful.length > 0) {
          message += `Successfully triggered ${successful.length} mail merge${successful.length > 1 ? 's' : ''}`;
        }
        if (alreadyRunning.length > 0) {
          message += (message ? ', ' : '') + `${alreadyRunning.length} already running`;
        }
        if (failed.length > 0) {
          message += `, ${failed.length} failed`;
        }
        showMessage(message);
        
        // Enable auto-refresh if not already enabled
        if (!autoRefresh && mergeJobs.some(job => notRunningJobs.includes(job.id) && !job.completed)) {
          setAutoRefresh(true);
          setRefreshInterval('6');
        }
        // Refresh to get updated job statuses
        await fetchData(false);
      } else {
        const allFailed = failed.every(f => f.error?.response?.status === 409);
        if (allFailed) {
          showMessage('All selected jobs are already running', 'error');
        } else {
          showMessage('Failed to trigger mail merges', 'error');
        }
      }
      
      // Clear selection after a delay to show the message
      if (selectedJobs.length > 0) {
        setTimeout(() => setSelectedJobs([]), 2000);
      }
    } catch (error) {
      console.error('Error triggering mail merge:', error);
      showMessage('Failed to trigger mail merges', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle add job
  const handleAddJob = async () => {
    try {
      // Debug logging
      console.log('Current targetConnects state:', targetConnects);
      console.log('Available clients:', clients);

      // Log all selected client IDs
      const selectedClientIds = Object.entries(targetConnects)
        .filter(([_, selected]) => selected === true)
        .map(([clientId]) => parseInt(clientId));
      console.log('Selected client IDs:', selectedClientIds);

      // Log the full client objects for selected IDs
      const selectedClientObjects = selectedClientIds.map(id => 
        clients.find(c => c.id === id)
      );
      console.log('Selected client objects:', selectedClientObjects);

      const selectedTargets = Object.entries(targetConnects)
        .filter(([_, selected]) => {
          console.log('Checking target selection:', { selected });
          return selected === true;
        })
        .map(([clientId]) => {
          const client = clients.find(c => c.id === parseInt(clientId));
          console.log('Processing client for job creation:', {
            clientId,
            client,
            connect_name: client?.connect_name
          });

          if (client?.connect_name === 'map100.travelminds.com') {
            console.log(`Client ${clientId} filtered out: map100.travelminds.com`);
            return null;
          }
          
          if (!client?.connect_name) {
            console.warn(`Client ${clientId} has no connect_name:`, client);
            return null;
          }
          
          return client.connect_name;
        })
        .filter((connectName): connectName is string => {
          const isValid = !!connectName;
          if (!isValid) {
            console.warn('Filtered out invalid connect_name');
          }
          return isValid;
        });

      console.log('Final selected targets:', selectedTargets);

      if (!sourceUrl || !emailId || selectedTargets.length === 0) {
        console.warn('Validation failed:', {
          sourceUrl,
          emailId,
          selectedTargetsLength: selectedTargets.length,
          targetConnects,
          selectedClientIds,
          selectedTargets
        });
        showMessage('Please select source URL, email ID, and at least one target', 'error');
        return;
      }

      const jobs = selectedTargets.map(targetUrl => {
        const client = clients.find(c => c.connect_name === targetUrl);
        return {
          source_email_id: parseInt(emailId),
          source_connect_url: sourceUrl,
          target_connect_url: targetUrl,
          client_id: client?.id
        };
      }).filter(job => job.client_id !== undefined);

      console.log('Creating jobs with data:', {
        jobs,
        sourceUrl,
        emailId,
        selectedTargets,
        targetConnects
      });
      
      try {
        await apiService.post('/api/merge-jobs', jobs);
        await fetchData(false);
        setAutoRefresh(true);
        setRefreshInterval('6');
        showMessage('Jobs created successfully');
      } catch (error: any) {
        console.error('Error creating merge jobs:', error);
        const errorMessage = error.details?.message || error.message || 'Failed to create merge jobs';
        showMessage(errorMessage, 'error');
        
        if (error.response?.data) {
          console.error('Server error details:', error.response.data);
        }
      }
    } catch (error) {
      console.error('Error in handleAddJob:', error);
      showMessage('An unexpected error occurred', 'error');
    }
  };

  // Calculate if all targets are selected (simple and fast)
  const areAllTargetsSelected = connectInstances.length > 0 && 
    connectInstances.every(client => targetConnects[client.id]);

  // Handle manual refresh
  const handleManualRefresh = () => {
    fetchData(false); // This will set loadingJobs to true
  };

  // Date range handling (matching analytics page)
  const handleDateRangeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newDateRange = event.target.value as DateRangeType;
    setShowCustomRange(newDateRange === 'custom');
    
    if (newDateRange !== 'custom') {
      // Calculate dates based on range type
      const end = new Date();
      const start = new Date();

      switch (newDateRange) {
        case 'last_1_day':
          start.setDate(end.getDate() - 1);
          break;
        case 'last_7_days':
          start.setDate(end.getDate() - 7);
          break;
        case 'last_30_days':
          start.setDate(end.getDate() - 30);
          break;
        case 'last_90_days':
          start.setDate(end.getDate() - 90);
          break;
        case 'last_12_months':
          start.setMonth(end.getMonth() - 12);
          break;
        default:
          start.setDate(end.getDate() - 30); // default to last 30 days
      }

      // Set time to start of day for start date and end of day for end date
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);

      setDateRange({
        date_range: newDateRange,
        startDate: start,
        endDate: end
      });
    }
  };

  const handleApplyCustomRange = () => {
    if (dateRange.startDate && dateRange.endDate) {
      setDateRange({
        date_range: 'custom',
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });
    }
  };

  // Clear date filters
  const clearDateFilters = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 1);  // Reset to last 1 day
    setDateRange({
      date_range: 'last_1_day',
      startDate: start,
      endDate: end
    });
    setShowCustomRange(false);
  };

  return (
    <PageContainer fullWidth>
      <Typography 
        variant="subtitle1" 
        color="text.secondary"
        sx={{ mb: 3 }}
      >
        Mail Merge
      </Typography>

      <Paper elevation={2} className="p-6" sx={{ width: '100%' }}>
        {/* Top Row: Source Connect URL, Email ID, Blank Space, and Group Selection - Standout Treatment */}
        <Paper 
          elevation={1} 
          className="p-4 mb-6" 
          sx={{ 
            width: '100%',
            backgroundColor: '#f8fafc',
            border: '2px solid #e2e8f0',
            borderRadius: '12px'
          }}
        >
          <Typography 
            variant="subtitle2" 
            sx={{ 
              mb: 2, 
              color: '#475569',
              fontWeight: 600,
              fontSize: '0.875rem',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}
          >
            Configuration
          </Typography>
          <Box className="flex gap-4">
          {/* Source Connect URL - 25% width */}
          <Box className="w-1/4">
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TextField
                fullWidth
                label="Source Connect URL"
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
                placeholder="map100.travelminds.com"
                size="small"
              />
              <Tooltip title="Open emails page in new tab">
                <IconButton
                  size="small"
                  onClick={() => window.open(`https://${sourceUrl}/s/emails`, '_blank')}
                  sx={{ 
                    minWidth: 'auto',
                    padding: '4px',
                    marginLeft: '4px'
                  }}
                >
                  <LaunchIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          {/* Email ID - 15% width */}
          <Box className="w-[15%]">
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TextField
                fullWidth
                label="Email ID"
                value={emailId}
                onChange={(e) => setEmailId(e.target.value)}
                type="number"
                size="small"
                error={!emailId && Object.values(targetConnects).some(Boolean)}
                inputProps={{
                  maxLength: 3,
                  style: { textAlign: 'center' }
                }}
              />
              <Tooltip title="Preview email">
                <IconButton
                  size="small"
                  onClick={() => {
                    if (emailId && sourceUrl) {
                      window.open(`https://${sourceUrl}/email/preview/${emailId}`, '_blank');
                    }
                  }}
                  disabled={!emailId || !sourceUrl}
                  sx={{ 
                    minWidth: 'auto',
                    padding: '4px',
                    marginLeft: '4px'
                  }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          {/* Blank Space - 45% width (flexible) */}
          <Box className="flex-grow">
            {/* Empty space - this will adjust on resize */}
          </Box>
          
          {/* Group Selection - 20% width */}
          <Box className="w-1/5">
            <FormControl fullWidth size="small">
              <InputLabel>Group Filter</InputLabel>
              <Select
                value={selectedGroup || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'ungrouped') {
                    handleGroupChange('ungrouped');
                  } else if (value === '') {
                    handleGroupChange(null);
                  } else {
                    handleGroupChange(Number(value));
                  }
                }}
                label="Group Filter"
                disabled={loadingGroupClients}
              >
                <MenuItem value="">
                  <em>None (All Clients)</em>
                </MenuItem>
                <MenuItem value="ungrouped">
                  <em>Clients Not in a Group</em>
                </MenuItem>
                {groups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name} ({group.client_count} clients)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {loadingGroupClients && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                <CircularProgress size={20} />
              </Box>
            )}
          </Box>
        </Box>
        </Paper>

        <Box className="flex flex-col md:flex-row gap-4" sx={{ width: '100%' }}>
          {/* Selection Box */}
          <Box className="w-full md:w-1/3" sx={{ width: '100%' }}>
            <Paper className="p-4" elevation={1} sx={{ width: '100%' }}>
              <Box className="space-y-3">
                <Box className="mt-1">
                  <Typography variant="subtitle1" className="mb-0" sx={{ marginTop: '-4px' }}>Target Connects</Typography>
                  <Box className="flex justify-between items-center mb-1">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={areAllTargetsSelected}
                          onChange={handleSelectAllTargets}
                          indeterminate={
                            Object.values(targetConnects).some(Boolean) && 
                            !areAllTargetsSelected
                          }
                          size="small"
                        />
                      }
                      label="Select All"
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleAddJob}
                      size="small"
                      disabled={!sourceUrl || !emailId || !Object.values(targetConnects).some(Boolean)}
                    >
                      Add {Object.values(targetConnects).filter(Boolean).length > 1 ? 'Jobs' : 'Job'}
                    </Button>
                  </Box>
                  <Paper variant="outlined" className="p-2">
                    <FormGroup className="gap-0">
                      {(loadingGroupClients || (loading && connectInstances.length === 0)) ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                          <CircularProgress size={24} />
                          <Typography variant="body2" sx={{ ml: 1, alignSelf: 'center' }}>
                            Loading targets...
                          </Typography>
                        </Box>
                      ) : (
                        connectInstances.map((client) => {
                          const isChecked = targetConnects[client.id] === true;
                          return (
                            <FormControlLabel
                              key={client.id}
                              control={
                                <Checkbox
                                  checked={isChecked}
                                  onChange={handleTargetConnectChange(client.id)}
                                  size="small"
                                />
                              }
                              label={client.url?.replace('.travelminds.com', '') || client.agency_name || `Client ${client.id}`}
                              sx={{ 
                                margin: 0,
                                '& .MuiTypography-root': {
                                  fontSize: '0.875rem',
                                  fontWeight: ((client.url || client.agency_name || '').toLowerCase().includes('travelminds') || 
                                             (client.url || client.agency_name || '').toLowerCase().includes('travel minds')) ? 
                                             'bold' : 'normal'
                                }
                              }}
                            />
                          );
                        })
                      )}
                    </FormGroup>
                  </Paper>
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* Jobs Box */}
          <Box className="w-full md:w-2/3" sx={{ width: '100%' }}>
            <Paper className="p-4" elevation={1} sx={{ width: '100%' }}>
              <Box className="mb-4">
                <Box className="flex justify-between items-center mb-3">
                  <Typography variant="h6">Jobs</Typography>
                  <Box className="flex items-center gap-3">
                  {/* Delete Selected button - only show when jobs are selected */}
                  {selectedJobs.length > 0 && (
                    <Button
                      variant="contained"
                      color="error"
                      onClick={handleDeleteSelected}
                      size="small"
                    >
                      Delete Selected ({selectedJobs.length})
                    </Button>
                  )}
                  
                  {/* Run Mail Merge button - always visible */}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleRunMailMerge}
                    size="small"
                    disabled={loading || mergeJobs.filter(job => !job.completed && !runningJobs.has(job.id)).length === 0}
                    sx={{
                      backgroundColor: '#10b981',
                      '&:hover': {
                        backgroundColor: '#059669',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#e2e8f0',
                        color: '#94a3b8',
                      },
                    }}
                  >
                    Run Mail Merge
                    {(() => {
                      const pendingNotRunning = mergeJobs.filter(job => !job.completed && !runningJobs.has(job.id)).length;
                      if (selectedJobs.length > 0) {
                        const selectedNotRunning = selectedJobs.filter(id => !runningJobs.has(id)).length;
                        return selectedNotRunning > 0 ? ` (${selectedNotRunning} selected)` : ` (${selectedJobs.length} running)`;
                      } else if (pendingNotRunning > 0) {
                        return ` (${pendingNotRunning} pending)`;
                      } else {
                        return runningJobs.size > 0 ? ` (${runningJobs.size} running)` : '';
                      }
                    })()}
                  </Button>
                  
                  {/* Auto-refresh controls - modern compact design */}
                  <Box className="flex items-center gap-2 ml-3 bg-gray-50 rounded-lg px-2 py-1" sx={{ height: '44px' }}>
                    {/* Auto-refresh toggle */}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={autoRefresh}
                          onChange={handleAutoRefreshChange}
                          size="small"
                          disabled={areAllJobsCompleted}
                          sx={{ padding: 0.5 }}
                        />
                      }
                      label="Auto"
                      title={areAllJobsCompleted ? "Auto-refresh disabled: all jobs are completed" : "Automatically refresh jobs data at regular intervals"}
                      sx={{ 
                        margin: 0, 
                        display: 'flex',
                        alignItems: 'center',
                        '& .MuiTypography-root': { 
                          fontSize: '0.75rem',
                          fontWeight: 500 
                        } 
                      }}
                    />
                    
                    {/* Interval input - shorter height, no arrows */}
                    <TextField
                      value={refreshInterval}
                      onChange={handleRefreshIntervalChange}
                      disabled={!autoRefresh || areAllJobsCompleted}
                      size="small"
                      variant="outlined"
                      type="number"
                      inputProps={{
                        min: 1,
                        max: 300,
                        step: 1,
                        style: {
                          textAlign: 'center',
                          padding: '0px 0px 0px 10px',
                          WebkitAppearance: 'none',
                          MozAppearance: 'textfield',
                          appearance: 'textfield',
                          margin: 0
                        }
                      }}
                      title="Interval in seconds between job data refreshes"
                      sx={{ 
                        margin: '5px 0px',
                        display: 'flex',
                        alignItems: 'center',
                        '& .MuiOutlinedInput-notchedOutline': { borderWidth: '1px' },
                        '& .MuiInputLabel-root': { display: 'none' },
                        '& .MuiInputBase-root': { 
                          height: '20px',
                          fontSize: '0.75rem'
                        }
                      }}
                    />
                    
                    {/* Compact refresh button - aligned center */}
                    <Tooltip title="Refresh jobs data">
                      <IconButton 
                        onClick={handleManualRefresh} 
                        size="small"
                        color="primary"
                        disabled={loading}
                        sx={{ 
                          p: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: '32px',
                          minHeight: '32px'
                        }}
                      >
                        {loading ? (
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            width: '20px',
                            height: '20px'
                          }}>
                            <CircularProgress size={20} />
                          </Box>
                        ) : (
                          <RefreshIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>

              {/* Date Range Filter - Analytics Style */}
              <Box className="mb-4">
                <Box className="flex items-center gap-4">
                  <Box className="flex items-center gap-2">
                    <DateRangeIcon className="text-gray-500" />
                    <Typography variant="body2" className="text-gray-700 whitespace-nowrap">
                      Date Range:
                    </Typography>
                  </Box>
                  
                  <FormControl size="small" className="min-w-[180px]">
                    <Select
                      value={dateRange.date_range}
                      onChange={handleDateRangeChange}
                      displayEmpty
                      className="bg-white"
                    >
                      <MenuItem value="last_1_day">Last 1 Day</MenuItem>
                      <MenuItem value="last_7_days">Last 7 Days</MenuItem>
                      <MenuItem value="last_30_days">Last 30 Days</MenuItem>
                      <MenuItem value="last_90_days">Last 90 Days</MenuItem>
                      <MenuItem value="last_12_months">Last 12 Months</MenuItem>
                      <MenuItem value="custom">Custom Range</MenuItem>
                    </Select>
                  </FormControl>
                  
                  {showCustomRange && (
                    <Box className="flex items-center gap-2">
                      <div>
                        <DatePicker
                          selected={dateRange.startDate}
                          onChange={(date: Date | null) => setDateRange(prev => ({ ...prev, startDate: date }))}
                          selectsStart
                          startDate={dateRange.startDate}
                          endDate={dateRange.endDate}
                          placeholderText="Start Date"
                          dateFormat="MMM d, yyyy"
                          className="custom-datepicker"
                          style={{
                            width: '150px',
                            padding: '8px 12px',
                            fontSize: '14px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            backgroundColor: 'white'
                          }}
                        />
                      </div>
                      <div>
                        <DatePicker
                          selected={dateRange.endDate}
                          onChange={(date: Date | null) => setDateRange(prev => ({ ...prev, endDate: date }))}
                          selectsEnd
                          startDate={dateRange.startDate}
                          endDate={dateRange.endDate}
                          minDate={dateRange.startDate}
                          placeholderText="End Date"
                          dateFormat="MMM d, yyyy"
                          className="custom-datepicker"
                          style={{
                            width: '150px',
                            padding: '8px 12px',
                            fontSize: '14px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            backgroundColor: 'white'
                          }}
                        />
                      </div>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleApplyCustomRange}
                        disabled={!dateRange.startDate || !dateRange.endDate}
                      >
                        Apply
                      </Button>
                    </Box>
                  )}
                  
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={clearDateFilters}
                    disabled={dateRange.date_range === 'last_1_day'}
                  >
                    Reset
                  </Button>
                  
                  <Typography variant="body2" color="text.secondary">
                    Showing {displayJobs.length} of {mergeJobs.length} jobs
                  </Typography>
                </Box>
              </Box>

              {loadingJobs ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} />
                  <Typography variant="body1" sx={{ ml: 2, alignSelf: 'center' }}>
                    Loading jobs...
                  </Typography>
                </Box>
              ) : (
                <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ width: '40px' }}>
                        {displayJobs.length > 0 && (
                          <Checkbox
                            checked={displayJobs.every(job => selectedJobs.includes(job.id))}
                            indeterminate={selectedJobs.some(jobId => displayJobs.some(job => job.id === jobId)) && !displayJobs.every(job => selectedJobs.includes(job.id))}
                            onChange={handleSelectAll}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ minWidth: '100px' }}>Created</TableCell>
                      <TableCell sx={{ minWidth: '50px' }}>Source</TableCell>
                      <TableCell sx={{ minWidth: '160px' }}>Target connect</TableCell>
                      <TableCell sx={{ minWidth: '60px' }}>Target</TableCell>
                      <TableCell sx={{ minWidth: '80px' }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayJobs.map((job) => (
                      <TableRow
                        key={job.id}
                        selected={selectedJobs.includes(job.id)}
                        hover
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox" sx={{ width: '40px' }}>
                          <Checkbox 
                            checked={selectedJobs.includes(job.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleJobSelection(job.id);
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: '120px' }}>{format(new Date(job.created), 'yyyy-MM-dd HH:mm:ss')}</TableCell>
                        <TableCell sx={{ minWidth: '60px' }}>
                          <a 
                            href={`https://${job.source_connect_url}/s/emails/view/${job.source_email_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            {job.source_email_id}
                          </a>
                        </TableCell>
                        <TableCell sx={{ minWidth: '160px' }}>
                          <a 
                            href={`https://${job.target_connect_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            {job.target_connect_url}
                          </a>
                        </TableCell>
                        <TableCell sx={{ minWidth: '60px' }}>
                          {job.target_email_id ? (
                            <a 
                              href={`https://${job.target_connect_url}/s/emails/view/${job.target_email_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              {job.target_email_id}
                            </a>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell sx={{ minWidth: '80px' }}>
                          {job.completed ? (
                            job.target_email_id ? (
                              <Tooltip 
                                title={format(new Date(job.completed), 'MMM dd, yyyy HH:mm:ss')}
                                placement="right"
                                followCursor
                              >
                                <Box
                                  component="span"
                                  className="px-2 py-1 rounded-full text-sm font-medium"
                                  sx={{
                                    backgroundColor: 'rgba(46, 160, 67, 0.1)',
                                    color: '#2ea043',
                                  }}
                                >
                                  <a 
                                    href={`https://${job.target_connect_url}/s/emails/view/${job.target_email_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="no-underline hover:underline"
                                  >
                                    Completed
                                  </a>
                                </Box>
                              </Tooltip>
                            ) : (
                              <Tooltip 
                                title={format(new Date(job.completed), 'MMM dd, yyyy HH:mm:ss')}
                                placement="right"
                                followCursor
                              >
                                <Box
                                  component="span"
                                  className="px-2 py-1 rounded-full text-sm font-medium"
                                  sx={{
                                    backgroundColor: 'rgba(207, 34, 46, 0.1)',
                                    color: '#cf222e',
                                  }}
                                >
                                  Failed
                                </Box>
                              </Tooltip>
                            )
                          ) : (
                            <Box
                              component="span"
                              className="px-2 py-1 rounded-full text-sm font-medium"
                              sx={{
                                backgroundColor: 'rgba(251, 191, 36, 0.1)',
                                color: '#d97706',
                              }}
                            >
                              Pending
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              )}
            </Box>
            </Paper>
          </Box>
        </Box>
      </Paper>
      <Snackbar
        open={snackbar.open}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default MailMerge; 